import axios from 'axios';
import { getCookie } from 'utils/storageUtils';
import { isBlankOrNullString } from 'utils/stringUtils';

// ---------------------------------------------------------------------------------------------------------------------

/**
 * If url is string type then replaces '{origin}' with window.location.origin.
 * @param {any} url
 * @return {any}
 */
const replaceOrigin = (url) => {
  if (typeof url == 'string') {
    return url.replace('{origin}', window.location.origin);
  }
  return url;
};

/**
 * Executes authentication once, Aborts all queued requests if it fails.
 * @param {AxiosInstance} instance
 * @param {string} route
 * @param {string} tokenStorageKey
 * @param {Set<AbortController>} controllers
 * @return {Promise<string>}
 */
const runAuth = async ({ instance, route, tokenStorageKey, controllers }) => {
  try {
    const { data } = await instance.get(route, {
      headers: { Channel: getCookie('channel') ?? 'Portal' },
    });

    if (isBlankOrNullString(data?.token)) {
      localStorage.removeItem(tokenStorageKey);
      throw new Error('Authentication failed');
    }

    localStorage.setItem(tokenStorageKey, data.token);
    return data.token;
  } catch (err) {
    localStorage.removeItem(tokenStorageKey);
    controllers.forEach((controller) => controller.abort());
    controllers.clear();
    throw err;
  }
};

// ---------------------------------------------------------------------------------------------------------------------

/**
 * @param {any} error
 * @return {string|undefined}
 */
export const extractErrorMessage = (error) => {
  if (typeof error == 'object') {
    if (typeof error.response?.data?.message == 'string' && error.response.data.message.trim().length > 0) {
      return error.response.data.message.trim();
    } else if (typeof error.message == 'string' && error.message.trim().length > 0) {
      return error.message.trim();
    }
  } else if (typeof error == 'string' && error.trim().length > 0) {
    return error.trim();
  }
  return undefined;
};

/**
 * Detects request cancellation across Axios versions.
 * @param {any} error
 * @return {boolean}
 */
export const isCanceledError = (error) => {
  return (
    axios.isCancel(error) ||
    (typeof error == 'object' && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError'))
  );
};

/**
 * Creates new axios instance with auth refresh token.
 * @param {string} baseURL
 * @param {string} authRoute
 * @param {string} tokenName
 * @param {string | undefined} [authBaseURL]
 * @param {number | undefined} [timeout]
 * @return {AxiosInstance}
 */
export const createAxiosWithAuth = ({ baseURL, authRoute, tokenName, authBaseURL, timeout }) => {
  const api = axios.create({
    baseURL: replaceOrigin(baseURL),
    timeout: timeout,
  });
  const authApi = axios.create({
    baseURL: replaceOrigin(authBaseURL ?? baseURL),
    timeout: timeout,
  });

  let authInFlight = null;
  let queuedControllers = new Set();
  const MAX_RETRIES = 3;

  /**
   * Single-flight auth wrapper.
   * @return {Promise<string>}
   */
  const authenticate = () => {
    if (!authInFlight) {
      authInFlight = runAuth({
        authApi: authApi,
        authRoute: authRoute,
        tokenName: tokenName,
        controllers: queuedControllers,
      }).finally(() => {
        authInFlight = null;
      });
    }
    return authInFlight;
  };

  // REQUEST interceptor
  api.interceptors.request.use(
    async (config) => {
      // Initialize header
      config.headers = config.headers ?? {};
      if (config.headers['X-RETRY'] == null) {
        config.headers['X-RETRY'] = 0;
      }
      if (config.headers['Channel'] == null) {
        config.headers['Channel'] = getCookie('channel') ?? 'Portal';
      }
      if (config.headers['LANG'] == null) {
        config.headers['LANG'] = getCookie('LANG') ?? undefined;
      }

      const controller = new AbortController();
      config.signal = controller.signal;

      let token = localStorage.getItem(tokenName);
      if (!token) {
        queuedControllers.add(controller);
        try {
          token = await authenticate();
        } finally {
          queuedControllers.delete(controller);
        }
      }

      config.headers.Authorization = token != null ? `Bearer ${token}` : config.headers.Authorization;
      return config;
    },
    (error) => Promise.reject(error)
  );

  // RESPONSE interceptor
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Cancellation should exit immediately
      if (isCanceledError(error)) {
        return Promise.reject(error);
      }

      const originalRequest = error.config;
      originalRequest.headers = originalRequest.headers ?? {};

      if (!error.response || error.response.status !== 401) {
        return Promise.reject(error);
      }

      const currentRetry = Number.parseInt(originalRequest.headers['X-RETRY'] ?? '0');

      if (currentRetry >= MAX_RETRIES) {
        return Promise.reject(error);
      }

      // Increment retry count
      originalRequest.headers['X-RETRY'] = currentRetry + 1;

      try {
        const token = await authenticate();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (authError) {
        return Promise.reject(authError);
      }
    }
  );

  return api;
};

export const createAxios = ({ baseURL, timeout }) => {
  const api = axios.create({
    baseURL: replaceOrigin(baseURL),
    timeout: timeout,
  });

  api.interceptors.request.use(
    async (config) => {
      // Initialize header
      config.headers = config.headers ?? {};
      if (config.headers['Channel'] == null) {
        config.headers['Channel'] = getCookie('channel') ?? 'Portal';
      }
      if (config.headers['LANG'] == null) {
        config.headers['LANG'] = getCookie('LANG') ?? undefined;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  return api;
};
